// Taken from Quickr src

/**
 * Matches a value against a set of conditions (value, result) and returns the result of the matched condition.
 * @example
 * ```ts
 * const arr = [1, 2, 3];
 * const result = match(arr.length,
 *    [3, () => true],
 *    [0, () => false],
 *    [, () => false]
 * );
 * console.log(result); // true
 * ```
 * @param value value to match
 * @param conditions conditions to match against value
 * @returns result of matched condition
 */
export const match = <T, R>(
    value: T,
    ...conditions: [T, (val?: T) => R][] | [undefined | null, () => R][]
): R => {
    const lastCondition = conditions?.[conditions.length - 1];
    if (!isCatchAll(lastCondition)) {
        throw new Error(
            "INVALID_MATCH_CONDITION: The last condition must be a catch-all (undefined or null).",
        );
    }
    for (const [condition, result] of conditions) {
        if (value === condition) {
            return result(value);
        }
    }
    return lastCondition?.[1]();
};

const isCatchAll = <T, R>(
    condition: [T, (val?: T) => R] | [undefined | null, () => R],
): condition is [undefined | null, () => R] => {
    return (
        typeof condition[0] === "undefined" ||
        (typeof condition[0] === "object" && condition[0] === null)
    );
};
